import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../helper/FormGenerator';
import { PageList, FormLayout } from '../components/PageComponents';
import { usePageAPI } from '../hooks/usePageAPI';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import axios from 'axios';
import { API_URL } from '../constants';
import { Rate, Toggle } from 'rsuite';
import { idFormat } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';

const Reviews = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages, fetchPage, createPage, updatePage, deletePage } = usePageAPI();
  const [formData, setFormData] = useState({ name: '' });
  const [pageData, setPageData] = useState([]);
  const [brandData, setTagData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [reload, setReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  const formFields = [
    { id: "name", label: "Tag Name", type: "text", placeholder: "Enter Tag Name", required: true },
  ];

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    const loadData = () => {
      setLoading(true);
      axios.get(`${API_URL}/reviews/all`, {
        params: { limit, currentPage: page, searchBy, searchValue, },
        headers: { 'Authorization': `Bearer ${adminAuthToken}` }
      })
        .then(response => { setPageData({ data: response.data.data, dataCount: response.data.dataCount }); })
        .catch(error => { console.error('Failed to fetch data:', error); })
        .finally(() => { setLoading(false); });
    };

    if (pageType.isList) {
      loadData();
    }

    if (pageType.isEdit) {
      const pageId = pathname.split('/').pop();
      fetchPage({ type: 'tags', pageId: pageId, setLoading: setLoading, setProgress: setProgress }).then(data => {
        setFormData({
          name: data.data.data.name || '',
        });
      });
    }
  }, [pathname, search, reload]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append('name', formData.name);

    const toastId = toast.loading(`Page ${pageType.isEdit ? 'updating ...' : 'creating ...'}`, { autoClose: false });

    try {
      const pageId = pageType.isEdit ? pathname.split('/').pop() : null;

      const result = pageType.isEdit
        ? await updatePage({ type: 'tags', pageId: pageId, formDataToSubmit: formDataToSubmit })
        : await createPage({ type: 'tags/', formDataToSubmit: formDataToSubmit });


      if (result.data.success == true) {
        toast.update(toastId, {
          render: `Category ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        navigate(`/admin/tag/edit/${pageType.isEdit ? pageId : result.data.id}`);
      } else {
        toast.update(toastId, {
          render: result.error,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || 'An unknown error occurred';

      // Update toast with error message
      toast.update(toastId, {
        render: errorMessage,  // Show the error message returned from the server
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      console.error('Error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      const toastId = toast.loading('Deleting review...', { autoClose: false });
  
      try {
        const response = await axios.delete(`${API_URL}/reviews/${id}`, {
          headers: {
            Authorization: `Bearer ${adminAuthToken}`,
          },
        });
  
        reload ? setReload(false) : setReload(true);

        toast.update(toastId, {
          render: 'Review deleted successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } catch (error) {
        console.error('Delete review error:', {
          message: error.message,
          response: error.response,
          status: error?.response?.status,
          data: error?.response?.data,
        });
  
        toast.update(toastId, {
          render: 'Failed to delete review.',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };  

  const handleStatusToggle = async (id, status) => {
    const toastId = toast.loading('Updating status...', { autoClose: false });

    try {
      await axios.put(`${API_URL}/reviews/${id}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${adminAuthToken}`,
          },
        }
      );

      toast.update(toastId, {
        render: 'Status updated successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Status update failed:', error);

      toast.update(toastId, {
        render: 'Failed to update status.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/tag/list', search: params.toString() });
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <PageList
            itemType={'reviews'}
            itemId={'id'}
            pageTitle={'Review List'}
            pageData={pageData.data}
            actionApplyKeyMap={{
              'Delete': 'delete',
            }}
            columns={['Product', 'Customer Name', 'Comment', 'Rating', 'State', 'Craeted at', 'Action']}
            filterBy={{ '': '', 'By Product Name': 'product_name', 'By Product Id': 'product_id', 'By Customer Name': 'customer_name', 'By Customer Id': 'customer_id' }}
            columnKeyMap={{
              'Product': (item) => <>{item.product_name} {idFormat(item.product_id)}</>,
              'Customer Name': (item) => <>{item.customer_name} {idFormat(item.customer_id)}</>,
              'Comment': 'comment',
              'Rating': (item) => (
                <Rate defaultValue={item.rating} color="orange" size='xs' disabled='true' />
              ),
              'State': (item) => (
                <Toggle
                  size="sm"
                  defaultChecked={item.status === true}
                  onChange={(checked) => handleStatusToggle(item.id, checked)}
                />
              ),
              'Craeted at': 'createdAt',
              'Action': (item) => (
                <span onClick={() => handleReviewDelete(item.id)}>
                  <i className='bi bi-trash'></i>
                </span>
              ),
            }}
            onAddNew={false}
            onView={false}
            onEdit={false}
            onDelete={false}
            totalRows={pageData.dataCount}
            updateUrlParams={updateUrlParams}
          />
      )}
    </>
  );
};

const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Tag" : isNew ? "Create New Tag" : "Page List";

export default Reviews;
